import { Check, ChevronsUpDown, PlusCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useGetCompanies } from '@/queries/company.queries';
import { useCompanyStore } from '@/store/useCompanyStore';
import { CompanyAvatar } from '@/components/atoms/CompanyAvatar';
import { APP_ROUTES } from '@/const/routes';

export const CompanySwitcher = () => {
  const { isMobile, state } = useSidebar();
  const { data: companies = [] } = useGetCompanies();
  const { activeCompanyId, setActiveCompanyId } = useCompanyStore();
  const handleSwitchCompany = (id: string) => {
    setActiveCompanyId(id);
  };

  // Resolve the active company: stored ID → fallback to real company → first in list
  const activeCompany =
    companies.find((c) => c.id === activeCompanyId) ??
    companies.find((c) => !c.isDemo) ??
    companies[0];

  if (!activeCompany) return null;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              tooltip={activeCompany.name}
            >
              <CompanyAvatar company={activeCompany} className="size-8" />
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium text-sm">{activeCompany.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {activeCompany.isDemo ? 'Explore with sample data' : 'Your company'}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 shrink-0 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : state === 'collapsed' ? 'right' : 'bottom'}
            align="start"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-2 py-1.5">
              Switch company
            </DropdownMenuLabel>

            {/* Real companies */}
            {companies.filter((c) => !c.isDemo).map((company) => {
              const isActive = activeCompany.id === company.id;
              return (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => handleSwitchCompany(company.id)}
                  className="gap-3 p-2 cursor-pointer"
                >
                  <CompanyAvatar company={company} className="size-8" />
                  <div className="grid flex-1 text-left leading-tight min-w-0">
                    <span className="font-medium text-sm truncate">{company.name}</span>
                    <span className="text-xs text-muted-foreground truncate">Your live data</span>
                  </div>
                  {isActive && <Check className="ml-auto size-4 text-secondary-accent shrink-0" />}
                </DropdownMenuItem>
              );
            })}

            {/* Divider + demo companies */}
            {companies.some((c) => c.isDemo) && (
              <>
                <DropdownMenuSeparator />
                {companies.filter((c) => c.isDemo).map((company) => {
                  const isActive = activeCompany.id === company.id;
                  return (
                    <DropdownMenuItem
                      key={company.id}
                      onClick={() => handleSwitchCompany(company.id)}
                      className="gap-3 p-2 cursor-pointer"
                    >
                      <CompanyAvatar company={company} className="size-8" />
                      <div className="grid flex-1 text-left leading-tight min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm truncate">{company.name}</span>
                          <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-secondary text-secondary-accent border border-secondary-accent/20">
                            Demo
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground truncate">Sample data · read only</span>
                      </div>
                      {isActive && <Check className="ml-auto size-4 text-secondary-accent shrink-0" />}
                    </DropdownMenuItem>
                  );
                })}
              </>
            )}

            {!companies.some((c) => !c.isDemo) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="gap-2 p-2 cursor-pointer">
                  <Link to={APP_ROUTES.app.onboarding} search={{ step: 1 }}>
                    <PlusCircle className="size-4" />
                    Add your company
                  </Link>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
